import React, { useState } from 'react';
import { ConfirmationModal, type ConfirmationModalProps } from '../ConfirmationModal';

export type PromiseConfirmationModalProps = Omit<ConfirmationModalProps, 'actionOnValidation'> & {
    /** Action asynchrone à exécuter lors de la validation */
    onConfirm: () => Promise<void>;
    /** Message à afficher pendant le chargement */
    loadingMessage?: string;
};

export const PromiseConfirmationModal: React.FunctionComponent<PromiseConfirmationModalProps> =
    function ({ onConfirm, messages, loadingMessage = 'Traitement en cours...', ...rest }) {
        const [isLoading, setIsLoading] = useState(false);

        const handleConfirm = async () => {
            setIsLoading(true);
            try {
                await onConfirm();
                // La fermeture serait gérée dans onConfirm
            } catch (error) {
                // L'erreur est déjà gérée par useApiRequest
                console.error(error);
                setIsLoading(false);
            }
        };

        // Modifier le message de validation si en cours de chargement
        const updatedMessages = {
            ...messages,
            validation: isLoading ? loadingMessage : messages?.validation,
            // Si en chargement, on peut aussi modifier le message de question pour indiquer l'état
            question: isLoading ? 'Traitement en cours...' : messages?.question,
            // Et modifier le message de détail pour montrer un indicateur visuel
            detail: isLoading
                ? {
                      message: 'Veuillez patienter pendant le traitement de votre demande.',
                      severity: 'info' as const,
                  }
                : messages?.detail,
        };

        const validationAction = isLoading ? () => {} : handleConfirm;
        const cancellationAction = isLoading ? () => {} : rest.actionOnCancellation;

        return (
            <ConfirmationModal
                {...rest}
                messages={updatedMessages}
                actionOnValidation={validationAction}
                actionOnCancellation={cancellationAction}
                disabled={isLoading}
                isFullWidth={rest.isFullWidth}
            />
        );
    };
